import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, FilePlus2, Copy, Check, User, Zap, Plus, Trash2, Search, PencilLine, FileText, Coins, AlertTriangle, Info, CheckCircle2, ArrowRightLeft, PlusCircle, Percent } from 'lucide-react';
import { UnidadeConsumidora } from '../../types';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';
import UCSearchPicker, { composeEnderecoUC } from './UCSearchPicker';
import UnidadeModal from './UnidadeModal';
import SignatariosSelector, { signatariosVazio } from '../../src/components/SignatariosSelector';
import { SignatariosEscolhidos } from '../../src/lib/termoSignatarios';
import { planosSelecionaveis, inferirPlanoPorPercentuais } from '../../src/lib/politicaComercial';
import ConfirmModal from '../../src/components/ConfirmModal';
import { useFechamentoSeguro } from '../../src/hooks/useFechamentoSeguro';
import { useCompletarDadosUc } from '../../src/hooks/useCompletarDadosUc';
import { carregarDadosDocumentoUc, sugerirSaldoUcs } from '../../src/services/dadosDocumentoUc';
import { fmtNumeroDistrato } from '../../src/lib/saldoDistrato';
import { ucCodigoKeys } from '../../src/lib/ucCodigo';
import { montarDadosDocumentoUc, seVazio, type DadosDocumentoUc } from '../../src/lib/dadosDocumentoUc';
import { aplicarTitularAditivo, mesmaPessoa } from '../../src/lib/titularesUc';

interface AditivoContratualModalProps {
    isOpen: boolean;
    onClose: () => void;
    unidade?: UnidadeConsumidora | null;
}

interface UCEntry {
    ucId?: number;
    numero: string;
    endereco: string;
}

type TipoAditivo = 'transferencia' | 'adicao' | 'desconto';

/** UC citada no aditivo que ainda não existe no cadastro. */
type UCFaltante = { numero: string; tipo: 'nova' | 'antiga' | 'desconto' };

type TermoStatus = 'assinado' | 'nao_assinado' | 'nao_encontrado';

interface TermoOriginalInfo {
    status: TermoStatus;
    dataAssinatura?: string; // ISO date
}

const MAX_UCS = 2;

const inputCls = "w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-blue-500 outline-none bg-white dark:bg-slate-900 dark:text-slate-100";
const labelCls = "block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider";
const sectionTitleCls = "text-sm font-bold text-gray-800 dark:text-slate-200 border-b border-gray-100 dark:border-slate-700 pb-2 flex items-center gap-2";

const appendObs = (atual: string | null | undefined, nota: string) =>
    atual && atual.trim() ? `${atual.trim()}\n${nota}` : nota;

const fmtBR = (iso: string) => {
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const AditivoContratualModal: React.FC<AditivoContratualModalProps> = ({ isOpen, onClose, unidade }) => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [successData, setSuccessData] = useState<{ link: string; numero: string } | null>(null);
    const [copied, setCopied] = useState(false);
    // Vira true no primeiro toque do operador. O preenchimento automatico pela UC
    // nao conta: nao ha trabalho a perder ainda.
    const [rascunho, setRascunho] = useState(false);

    // Tipo do aditivo: transferência (desvincula antigas + vincula novas), apenas adição
    // de UC, ou alteração do desconto contratual das UCs já vinculadas.
    const [tipoAditivo, setTipoAditivo] = useState<TipoAditivo>('transferencia');

    // Modo de preenchimento
    const [modo, setModo] = useState<'buscar' | 'manual'>('buscar');
    const [ucsSelecionadas, setUcsSelecionadas] = useState<UnidadeConsumidora[]>([]);

    // UCs do aditivo que ainda não existem no cadastro — exigem registro completo via UnidadeModal
    const [ucsFaltantes, setUcsFaltantes] = useState<UCFaltante[]>([]);
    const [cadastroPrefill, setCadastroPrefill] = useState<Partial<UnidadeConsumidora> | null>(null);
    const [showCadastroUC, setShowCadastroUC] = useState(false);
    const [cadastroNumero, setCadastroNumero] = useState('');

    // Titular atual
    const [titularAtualNome, setTitularAtualNome] = useState('');
    const [titularAtualCpfCnpj, setTitularAtualCpfCnpj] = useState('');

    // Novo titular (pré-preenchido com o titular atual — edite apenas se houver troca)
    const [novoTitularNome, setNovoTitularNome] = useState('');
    const [novoTitularCpfCnpj, setNovoTitularCpfCnpj] = useState('');
    const [tipoCliente, setTipoCliente] = useState<'PF' | 'PJ'>('PF');
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');

    // Dados do termo
    const [dataTermoOriginal, setDataTermoOriginal] = useState('');
    const [termoInfo, setTermoInfo] = useState<TermoOriginalInfo | null>(null);
    const [dataTransferencia, setDataTransferencia] = useState(new Date().toISOString().split('T')[0]);
    const [concessionaria, setConcessionaria] = useState('Equatorial Goiás');

    // UCs antigas (a desvincular) e novas (a vincular)
    const emptyEntry = (): UCEntry => ({ numero: '', endereco: '' });

    // Tooltip state for info icons
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
    const toggleTooltip = (id: string) => setActiveTooltip(prev => prev === id ? null : id);
    const [ucsAntigas, setUcsAntigas] = useState<UCEntry[]>([emptyEntry()]);
    const [ucsNovas, setUcsNovas] = useState<UCEntry[]>([emptyEntry()]);

    // Saldo de créditos
    const [saldoKwh, setSaldoKwh] = useState('');
    const [valorSaldo, setValorSaldo] = useState('');

    // Alteração de desconto — percentuais em escala de exibição (20, 50), igual à
    // UnidadeForm. No banco ficam como fração (0.2, 0.5).
    const [descontoAtualTarifa, setDescontoAtualTarifa] = useState<number | null>(null);
    const [descontoAtualBandeira, setDescontoAtualBandeira] = useState<number | null>(null);
    const [descontoNovoTarifa, setDescontoNovoTarifa] = useState('');
    const [descontoNovoBandeira, setDescontoNovoBandeira] = useState('');
    // Grava o novo desconto na UC assim que o aditivo é gerado — mesmo comportamento
    // das outras opções, que já sincronizam o cadastro na hora.
    const [aplicarDescontoNoCadastro, setAplicarDescontoNoCadastro] = useState(true);

    // Observações
    const [obs, setObs] = useState('');

    // Representante legal + testemunhas que assinam junto com o associado
    const [signatarios, setSignatarios] = useState<SignatariosEscolhidos>(signatariosVazio);

    const handleCpfCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/\D/g, '');
        if (tipoCliente === 'PF') {
            if (val.length > 11) val = val.slice(0, 11);
            val = val.replace(/(\d{3})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        } else {
            if (val.length > 14) val = val.slice(0, 14);
            val = val.replace(/^(\d{2})(\d)/, '$1.$2');
            val = val.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
            val = val.replace(/\.(\d{3})(\d)/, '.$1/$2');
            val = val.replace(/(\d{4})(\d)/, '$1-$2');
        }
        setNovoTitularCpfCnpj(val);
    };

    const aplicarDadosCliente = (uc: UnidadeConsumidora) => {
        // Nome e CPF/CNPJ do mesmo cliente — ver montarDadosDocumentoUc.
        const d = montarDadosDocumentoUc(uc);
        setTitularAtualNome(d.nome);
        setTitularAtualCpfCnpj(d.cpfCnpj);
        // Novo titular começa igual ao atual — o operador altera apenas quando houver troca de titularidade
        setNovoTitularNome(d.nome);
        setNovoTitularCpfCnpj(d.cpfCnpj);
        setTipoCliente(d.tipoCliente);
        setEmail(d.email);
        setWhatsapp(d.whatsapp);
        // Banco guarda fração (0.2); a tela trabalha em percentual (20)
        setDescontoAtualTarifa(uc.desconto_tarifa != null ? Math.round(uc.desconto_tarifa * 100) : null);
        setDescontoAtualBandeira(uc.desconto_bandeira != null ? Math.round(uc.desconto_bandeira * 100) : null);
        if (uc.concessionaria) setConcessionaria(uc.concessionaria);
    };

    // O que o cadastro da UC não tem vem da negociação e da última fatura,
    // e só entra em campo vazio.
    const { completar, esquecer } = useCompletarDadosUc();
    const aplicarComplemento = (uc: UnidadeConsumidora, d: DadosDocumentoUc) => {
        setTitularAtualNome(seVazio(d.nome));
        setNovoTitularNome(seVazio(d.nome));
        if (!montarDadosDocumentoUc(uc).cpfCnpj && d.cpfCnpj) {
            setTitularAtualCpfCnpj(seVazio(d.cpfCnpj));
            setNovoTitularCpfCnpj(seVazio(d.cpfCnpj));
            setTipoCliente(d.tipoCliente);
        }
        setEmail(seVazio(d.email));
        setWhatsapp(seVazio(d.whatsapp));
        completarEnderecoDaUc(uc, d);
    };
    /** Endereço da linha da UC na lista de UCs do aditivo. */
    const completarEnderecoDaUc = (uc: UnidadeConsumidora, d: DadosDocumentoUc) => {
        const preencher = seVazio(d.enderecoTexto, composeEnderecoUC(uc));
        setUcsAntigas(prev => prev.map(e => e.ucId === uc.id ? { ...e, endereco: preencher(e.endereco) } : e));
    };

    const copiarTitularAtual = () => {
        setNovoTitularNome(titularAtualNome);
        setNovoTitularCpfCnpj(titularAtualCpfCnpj);
        setTipoCliente(titularAtualCpfCnpj.replace(/\D/g, '').length === 14 ? 'PJ' : 'PF');
    };

    const carregarTermoOriginal = async (uc: UnidadeConsumidora) => {
        try {
            let neg: { id: number; data_assinatura_termo: string | null } | null = null;

            if (uc.negociacao_id) {
                const { data } = await supabase
                    .from('negociacoes')
                    .select('id, data_assinatura_termo')
                    .eq('id', uc.negociacao_id)
                    .maybeSingle();
                neg = data;
            }
            if (!neg && uc.numero_uc) {
                const { data } = await supabase
                    .from('negociacoes')
                    .select('id, data_assinatura_termo')
                    .eq('numero_uc', uc.numero_uc)
                    .order('created_at', { ascending: false })
                    .limit(1);
                neg = data?.[0] || null;
            }

            if (!neg) {
                setTermoInfo({ status: 'nao_encontrado' });
                return;
            }
            if (!neg.data_assinatura_termo) {
                setTermoInfo({ status: 'nao_assinado' });
                return;
            }
            const isoDate = neg.data_assinatura_termo.split('T')[0];
            setTermoInfo({ status: 'assinado', dataAssinatura: isoDate });
            setDataTermoOriginal(isoDate);
        } catch (err) {
            console.error('Erro ao buscar termo original:', err);
            setTermoInfo({ status: 'nao_encontrado' });
        }
    };

    useEffect(() => {
        if (isOpen) {
            setSuccessData(null);
            setRascunho(false);
            setTipoAditivo('transferencia');
            setNovoTitularNome('');
            setNovoTitularCpfCnpj('');
            setTipoCliente('PF');
            setEmail('');
            setWhatsapp('');
            setObs('');
            setDataTermoOriginal('');
            setTermoInfo(null);
            setDataTransferencia(new Date().toISOString().split('T')[0]);
            setSaldoKwh('');
            setValorSaldo('');
            setDescontoAtualTarifa(null);
            setDescontoAtualBandeira(null);
            setDescontoNovoTarifa('');
            setDescontoNovoBandeira('');
            setAplicarDescontoNoCadastro(true);
            setUcsNovas([emptyEntry()]);
            setUcsFaltantes([]);
            setCadastroPrefill(null);
            setShowCadastroUC(false);
            setActiveTooltip(null);
            setSignatarios(signatariosVazio);

            if (unidade) {
                setModo('buscar');
                setUcsSelecionadas([unidade]);
                aplicarDadosCliente(unidade);
                setUcsAntigas([{
                    ucId: unidade.id,
                    numero: unidade.numero_uc || '',
                    endereco: composeEnderecoUC(unidade)
                }]);
                carregarTermoOriginal(unidade);
                completar(unidade, d => aplicarComplemento(unidade, d));
            } else {
                esquecer();
                setModo('buscar');
                setUcsSelecionadas([]);
                setTitularAtualNome('');
                setTitularAtualCpfCnpj('');
                setConcessionaria('Equatorial Goiás');
                setUcsAntigas([emptyEntry()]);
            }
        }
    }, [isOpen, unidade]);

    // Saldo de créditos da(s) UC(s) antiga(s) pela conta mais recente — a mesma
    // sugestão do distrato. Só entra em campo vazio ou no valor que este efeito
    // pôs; o digitado fica. Aditivo de desconto não tem saldo.
    const chavesSaldo = JSON.stringify(ucsAntigas
        .filter(u => u.numero.trim())
        .map(u => {
            const cadastro = ucsSelecionadas.find(x => x.id === u.ucId);
            return { chaves: ucCodigoKeys(u.numero, cadastro?.outro_numero_uc), desconto: cadastro?.desconto_tarifa ?? null };
        })
        .filter(u => u.chaves.length > 0));
    const saldoAuto = useRef({ kwh: '', valor: '' });
    useEffect(() => {
        if (!isOpen) { saldoAuto.current = { kwh: '', valor: '' }; return; }
        if (tipoAditivo === 'desconto') return;
        const lista = JSON.parse(chavesSaldo) as { chaves: string[]; desconto: number | null }[];
        if (lista.length === 0) return;
        let vivo = true;
        sugerirSaldoUcs(lista)
            .then(sug => {
                if (!vivo || !sug || sug.kwh === null) return;
                const kwh = fmtNumeroDistrato(sug.kwh);
                const valor = sug.valor !== null ? fmtNumeroDistrato(sug.valor) : '';
                setSaldoKwh(seVazio(kwh, saldoAuto.current.kwh));
                setValorSaldo(seVazio(valor, saldoAuto.current.valor));
                saldoAuto.current = { kwh, valor };
            })
            .catch(err => console.error('Sugestão de saldo do aditivo', err));
        return () => { vivo = false; };
    }, [isOpen, tipoAditivo, chavesSaldo]);

    const handleClose = () => {
        setSuccessData(null);
        onClose();
    };

    // Depois de gerado nao ha mais o que perder — o sucesso fecha sem perguntar.
    const { propsDoFundo, pedirParaFechar, confirmandoDescarte, descartarEFechar, continuarPreenchendo } =
        useFechamentoSeguro({ isOpen, temRascunho: rascunho && !successData, onFechar: handleClose });

    if (!isOpen) return null;

    // ── Alteração de desconto ────────────────────────────────────────────────
    const ehDesconto = tipoAditivo === 'desconto';
    // Aqui não existe UC "antiga"/"nova": a mesma lista guarda as UCs abrangidas,
    // reaproveitando o que veio da busca.
    const ucsDesconto = ucsAntigas.filter(u => u.numero.trim());
    const novoTarifaNum = descontoNovoTarifa.trim() === '' ? null : Number(descontoNovoTarifa);
    const novoBandeiraNum = descontoNovoBandeira.trim() === '' ? null : Number(descontoNovoBandeira);
    const descontoNovoPreenchido = novoTarifaNum != null && novoBandeiraNum != null
        && !Number.isNaN(novoTarifaNum) && !Number.isNaN(novoBandeiraNum);
    const planoNovo = descontoNovoPreenchido
        ? inferirPlanoPorPercentuais(novoTarifaNum as number, novoBandeiraNum as number)
        : null;
    const textoDescontoAtual = (descontoAtualTarifa != null || descontoAtualBandeira != null)
        ? `${descontoAtualTarifa ?? 0}% tarifa + ${descontoAtualBandeira ?? 0}% bandeira`
        : 'não informado';
    const textoDescontoNovo = descontoNovoPreenchido
        ? `${novoTarifaNum}% tarifa + ${novoBandeiraNum}% bandeira`
        : '';

    /** Trocar o tipo depois de já ter selecionado UC não pode deixar a lista vazia. */
    const alterarTipoAditivo = (t: TipoAditivo) => {
        setTipoAditivo(t);
        if (t !== 'adicao' && ucsSelecionadas.length > 0) {
            setUcsAntigas(ucsSelecionadas.slice(0, MAX_UCS).map(uc => ({
                ucId: uc.id,
                numero: uc.numero_uc || '',
                endereco: composeEnderecoUC(uc)
            })));
        }
    };

    const handleAddUcSelecionada = (uc: UnidadeConsumidora) => {
        setRascunho(true);
        setUcsSelecionadas(prev => [...prev, uc]);
        if (tipoAditivo === 'transferencia' || tipoAditivo === 'desconto') {
            const entry: UCEntry = {
                ucId: uc.id,
                numero: uc.numero_uc || '',
                endereco: composeEnderecoUC(uc)
            };
            setUcsAntigas(prev => {
                const preenchidas = prev.filter(e => e.numero || e.endereco);
                return [...preenchidas, entry].slice(0, MAX_UCS);
            });
        }
        if (ucsSelecionadas.length === 0) {
            aplicarDadosCliente(uc);
            carregarTermoOriginal(uc);
            completar(uc, d => aplicarComplemento(uc, d));
        } else {
            // UC a mais do mesmo cliente: só o endereço da linha dela.
            carregarDadosDocumentoUc(uc).then(d => completarEnderecoDaUc(uc, d));
        }
    };

    const handleRemoveUcSelecionada = (uc: UnidadeConsumidora) => {
        setRascunho(true);
        setUcsSelecionadas(prev => prev.filter(s => s.id !== uc.id));
        setUcsAntigas(prev => {
            const restantes = prev.filter(e => e.ucId !== uc.id);
            return restantes.length > 0 ? restantes : [emptyEntry()];
        });
    };

    const updateEntry = (
        setter: React.Dispatch<React.SetStateAction<UCEntry[]>>,
        index: number,
        field: keyof UCEntry,
        value: string
    ) => {
        setter(prev => prev.map((e, i) => i === index ? { ...e, [field]: value } : e));
    };

    const buscarUcPorNumero = async (numero: string) => {
        const { data } = await supabase
            .from('unidades_consumidoras')
            .select('id, observacoes_uc')
            .eq('numero_uc', numero.trim())
            .order('created_at', { ascending: false })
            .limit(1);
        return data?.[0] || null;
    };

    /**
     * Toda UC do aditivo precisa existir no cadastro completo. As que não existirem
     * são listadas para registro via UnidadeModal (formulário completo do CRM),
     * pré-preenchido clonando a UC base do cliente — nada de registro pela metade.
     */
    const verificarUcsCadastradas = async (antigas: UCEntry[], novas: UCEntry[], doDesconto: UCEntry[] = []) => {
        const faltantes: UCFaltante[] = [];
        for (const u of antigas) {
            if (!u.ucId && !(await buscarUcPorNumero(u.numero))) {
                faltantes.push({ numero: u.numero.trim(), tipo: 'antiga' });
            }
        }
        for (const u of novas) {
            if (!(await buscarUcPorNumero(u.numero))) {
                faltantes.push({ numero: u.numero.trim(), tipo: 'nova' });
            }
        }
        for (const u of doDesconto) {
            if (!u.ucId && !(await buscarUcPorNumero(u.numero))) {
                faltantes.push({ numero: u.numero.trim(), tipo: 'desconto' });
            }
        }
        return faltantes;
    };

    const abrirCadastroUC = async (faltante: UCFaltante) => {
        // Clona a UC base do cliente para o operador não recomeçar o cadastro do zero
        let base: any = null;
        const baseId = ucsSelecionadas[0]?.id || ucsAntigas.find(u => u.ucId)?.ucId;
        if (baseId) {
            const { data } = await supabase.from('unidades_consumidoras').select('*').eq('id', baseId).maybeSingle();
            base = data;
        }
        const hojeBR = fmtBR(new Date().toISOString().split('T')[0]);
        const herdado: Partial<UnidadeConsumidora> = base ? {
            segmento: base.segmento || undefined,
            concessionaria: base.concessionaria || concessionaria || undefined,
            tipo_ligacao: base.tipo_ligacao || undefined,
            grupo_tarifario: base.grupo_tarifario || undefined,
            // UnidadeForm exibe descontos em percentual (20, 50); no banco ficam como fração (0.2, 0.5)
            desconto_tarifa: base.desconto_tarifa ? Math.round(base.desconto_tarifa * 100) : undefined,
            desconto_bandeira: base.desconto_bandeira ? Math.round(base.desconto_bandeira * 100) : undefined,
            carencia: base.carencia || undefined,
            valor_tarifa: base.valor_tarifa || undefined,
            consumo_medio_kwh: base.consumo_medio_kwh || undefined,
            negociacao_id: base.negociacao_id || undefined,
            cpf_cnpj_cliente: base.cpf_cnpj_cliente || undefined,
            data_nascimento_cliente: base.data_nascimento_cliente || undefined,
            outro_email: base.outro_email || undefined
        } : { concessionaria: concessionaria || undefined };

        if (faltante.tipo === 'nova') {
            // Titular do aditivo diferente do cliente da UC base: o CPF, o nascimento e
            // o e-mail extra herdados são da outra pessoa. Nome de um com documento do
            // outro é o que fez boleto sair no CNPJ errado (ver src/lib/pagadorBoleto.ts).
            const titularMudou = !!base && !mesmaPessoa(
                { nome: base.nome_cliente, documento: base.cpf_cnpj_cliente },
                { nome: novoTitularNome, documento: novoTitularCpfCnpj }
            );
            setCadastroPrefill({
                ...herdado,
                ...(titularMudou ? {
                    cpf_cnpj_cliente: novoTitularCpfCnpj || undefined,
                    data_nascimento_cliente: undefined,
                    outro_email: undefined
                } : {}),
                numero_uc: faltante.numero,
                nome_cliente: novoTitularNome || base?.nome_cliente || '',
                titular_fatura: novoTitularNome || '',
                cpf_cnpj_titular: novoTitularCpfCnpj || '',
                email_cliente: email || base?.email_cliente || '',
                whatsapp_cliente: whatsapp || base?.whatsapp_cliente || '',
                situacao: null,
                observacoes_uc: `Vinculada via Aditivo Contratual em ${hojeBR}.`
            });
        } else {
            // Antiga (transferência) entra Cancelada; a do aditivo de desconto continua ativa.
            setCadastroPrefill({
                ...herdado,
                numero_uc: faltante.numero,
                nome_cliente: titularAtualNome || base?.nome_cliente || '',
                titular_fatura: titularAtualNome || '',
                cpf_cnpj_titular: titularAtualCpfCnpj || '',
                email_cliente: base?.email_cliente || email || '',
                whatsapp_cliente: base?.whatsapp_cliente || whatsapp || '',
                situacao: faltante.tipo === 'desconto' ? null : 'Cancelada',
                observacoes_uc: faltante.tipo === 'desconto'
                    ? `Cadastrada para o Aditivo de alteração de desconto em ${hojeBR}.`
                    : `UC antiga cadastrada para registro do Aditivo Contratual em ${hojeBR} (será desvinculada).`
            });
        }
        setCadastroNumero(faltante.numero);
        setShowCadastroUC(true);
    };

    const handleCadastroSalvo = () => {
        setUcsFaltantes(prev => prev.filter(f => f.numero !== cadastroNumero));
        setCadastroPrefill(null);
        addToast(`UC ${cadastroNumero} cadastrada. Clique em "Gerar Aditivo" para continuar.`, 'success');
    };

    /**
     * Após gerar o aditivo, reflete o resultado no cadastro (todas as UCs já existem):
     * - UCs novas: titular atualizado e situação Ativa
     * - UCs antigas (transferência): situação Cancelada
     */
    const sincronizarUnidades = async (antigas: UCEntry[], novas: UCEntry[], numeroTermo: string) => {
        const hojeBR = fmtBR(new Date().toISOString().split('T')[0]);
        const sufixoTermo = numeroTermo ? ` (termo ${numeroTermo})` : '';
        const erros: string[] = [];

        if (tipoAditivo === 'transferencia') {
            for (const uc of antigas) {
                try {
                    const alvo = await buscarUcPorNumero(uc.numero);
                    if (!alvo) { erros.push(uc.numero); continue; }
                    const { error } = await supabase.from('unidades_consumidoras')
                        .update({
                            situacao: 'Cancelada',
                            observacoes_uc: appendObs(alvo.observacoes_uc, `Desvinculada via Aditivo Contratual em ${hojeBR}${sufixoTermo}.`)
                        })
                        .eq('id', alvo.id);
                    if (error) throw error;
                } catch (err) {
                    console.error(`Erro ao sincronizar UC antiga ${uc.numero}:`, err);
                    erros.push(uc.numero);
                }
            }
        }

        // A vinculação passa pelo banco (aplicar_titular_aditivo_uc), não por update
        // direto: se o titular do aditivo é outra pessoa numa UC que já tem cobrança,
        // as faturas e os acordos anteriores ficam com quem saiu — mesmo corte da
        // troca de dono — e a mudança entra no histórico de titulares da UC (#6238).
        const avisos: string[] = [];
        for (const uc of novas) {
            try {
                const alvo = await buscarUcPorNumero(uc.numero);
                if (!alvo) { erros.push(uc.numero); continue; }
                const r = await aplicarTitularAditivo(supabase, {
                    ucId: alvo.id,
                    nome: novoTitularNome,
                    documento: novoTitularCpfCnpj,
                    email: email || null,
                    whatsapp: whatsapp || null,
                    numeroTermo: numeroTermo || null,
                });
                if (r.trocou) {
                    const anterior = r.titular_anterior?.trim() || 'o titular anterior';
                    const separadas = (r.faturas_separadas || 0) + (r.acordos_separados || 0);
                    avisos.push(`UC ${uc.numero}: o titular passou de ${anterior} para ${novoTitularNome}.`
                        + (separadas > 0 ? ` ${r.faturas_separadas || 0} fatura(s) e ${r.acordos_separados || 0} acordo(s) já cobrados continuam no nome de ${anterior}.` : ''));
                    if (r.assinante_negociacao) {
                        avisos.push(`A negociação #${r.negociacao_id} da UC ${uc.numero} continua no nome de ${r.assinante_negociacao}, e o boleto sai no nome de quem está na negociação. Atualize o cliente da negociação para o novo titular.`);
                    }
                }
            } catch (err: any) {
                console.error(`Erro ao sincronizar UC nova ${uc.numero}:`, err);
                erros.push(err?.message ? `${uc.numero} (${err.message})` : uc.numero);
            }
        }

        if (erros.length > 0) {
            addToast(`Aditivo gerado, mas falhou a sincronização das UCs: ${erros.join(', ')}. Ajuste manualmente em Unidades.`, 'warning', 12000);
        } else {
            addToast('Unidades consumidoras sincronizadas no sistema.', 'success');
        }
        avisos.forEach(a => addToast(a, 'warning', 15000));
    };

    /**
     * Aditivo de desconto: grava o novo percentual nas UCs abrangidas (o banco guarda
     * fração) e deixa a mudança registrada nas observações da UC.
     */
    const sincronizarDesconto = async (ucs: UCEntry[], numeroTermo: string) => {
        const hojeBR = fmtBR(new Date().toISOString().split('T')[0]);
        const sufixoTermo = numeroTermo ? ` (termo ${numeroTermo})` : '';
        const erros: string[] = [];

        for (const uc of ucs) {
            try {
                const alvo = await buscarUcPorNumero(uc.numero);
                if (!alvo) { erros.push(uc.numero); continue; }
                const { error } = await supabase.from('unidades_consumidoras')
                    .update({
                        desconto_tarifa: (novoTarifaNum as number) / 100,
                        desconto_bandeira: (novoBandeiraNum as number) / 100,
                        observacoes_uc: appendObs(
                            alvo.observacoes_uc,
                            `Desconto alterado de ${textoDescontoAtual} para ${textoDescontoNovo} via Aditivo Contratual em ${hojeBR}${sufixoTermo} — vigência ${fmtBR(dataTransferencia)}.`
                        )
                    })
                    .eq('id', alvo.id);
                if (error) throw error;
            } catch (err) {
                console.error(`Erro ao aplicar o novo desconto na UC ${uc.numero}:`, err);
                erros.push(uc.numero);
            }
        }

        if (erros.length > 0) {
            addToast(`Aditivo gerado, mas o novo desconto não foi aplicado em: ${erros.join(', ')}. Ajuste manualmente em Unidades.`, 'warning');
        } else {
            addToast(`Novo desconto (${textoDescontoNovo}) aplicado no cadastro das UCs.`, 'success');
        }
    };

    const handleGenerateAditivo = async (e: React.FormEvent) => {
        e.preventDefault();

        const antigasValidas = tipoAditivo === 'transferencia' ? ucsAntigas.filter(u => u.numero.trim()) : [];
        const novasValidas = ehDesconto ? [] : ucsNovas.filter(u => u.numero.trim());

        if (!novoTitularNome || !novoTitularCpfCnpj || !email || !whatsapp) {
            addToast('Preencha os dados do titular (Nome, CPF/CNPJ, E-mail e WhatsApp).', 'warning');
            return;
        }
        if (!dataTermoOriginal) {
            addToast('Informe a Data do Termo de Adesão Original.', 'warning');
            return;
        }
        if (!dataTransferencia) {
            addToast(ehDesconto ? 'Informe a Data de Vigência do novo desconto.' : 'Informe a Data da Transferência.', 'warning');
            return;
        }
        if (tipoAditivo === 'transferencia' && antigasValidas.length === 0) {
            addToast('Informe ao menos uma UC antiga (a ser desvinculada) ou mude para "Apenas Adição".', 'warning');
            return;
        }
        if (!ehDesconto && novasValidas.length === 0) {
            addToast('Informe ao menos uma UC nova (a ser vinculada).', 'warning');
            return;
        }
        if (ehDesconto) {
            if (ucsDesconto.length === 0) {
                addToast('Informe ao menos uma UC abrangida pelo aditivo.', 'warning');
                return;
            }
            if (!descontoNovoPreenchido) {
                addToast('Informe o novo desconto (tarifa e bandeira).', 'warning');
                return;
            }
            const t = novoTarifaNum as number, b = novoBandeiraNum as number;
            if (t < 0 || t > 100 || b < 0 || b > 100) {
                addToast('Os percentuais de desconto devem ficar entre 0% e 100%.', 'warning');
                return;
            }
            if (t === descontoAtualTarifa && b === descontoAtualBandeira) {
                addToast('O novo desconto é igual ao atual — não há o que alterar.', 'warning');
                return;
            }
        }
        if (!signatarios.procurador || !signatarios.testemunha1 || !signatarios.testemunha2) {
            addToast('Selecione o representante legal e as duas testemunhas do aditivo.', 'warning');
            return;
        }
        if (termoInfo?.status === 'nao_assinado') {
            addToast('Atenção: o termo de adesão original ainda não foi assinado. O aditivo será gerado mesmo assim — confirme se é o desejado.', 'warning');
        }

        setLoading(true);

        try {
            // Toda UC do termo precisa existir no cadastro — sem isso o sistema fica incoerente
            const faltantes = await verificarUcsCadastradas(antigasValidas, novasValidas, ehDesconto ? ucsDesconto : []);
            if (faltantes.length > 0) {
                setUcsFaltantes(faltantes);
                addToast('Há UC(s) que não existem no cadastro. Cadastre-as pelo painel acima antes de gerar o aditivo.', 'warning');
                setLoading(false);
                return;
            }
            setUcsFaltantes([]);

            const { data: settings } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'aditivo_contratual_webhook_url')
                .single();

            const webhookUrl = settings?.value || '';

            if (!webhookUrl) {
                addToast('URL do Webhook de Aditivo Contratual não configurada em Config > APIs.', 'error');
                setLoading(false);
                return;
            }

            const payload = {
                tipo_aditivo: tipoAditivo,
                uc_id: antigasValidas[0]?.ucId || ucsDesconto[0]?.ucId || ucsSelecionadas[0]?.id || null,
                uc_ids: (ehDesconto ? ucsDesconto : antigasValidas).map(u => u.ucId).filter(Boolean),
                titular_atual_nome: titularAtualNome,
                titular_atual_cpf_cnpj: titularAtualCpfCnpj,
                novo_titular_nome: novoTitularNome,
                novo_titular_cpf_cnpj: novoTitularCpfCnpj,
                tipo_cliente: tipoCliente,
                email: email,
                whatsapp: whatsapp,
                data_termo_original: dataTermoOriginal,
                data_transferencia: dataTransferencia,
                saldo_kwh: saldoKwh,
                valor_saldo: valorSaldo,
                nome_concessionaria: concessionaria,
                obs: obs,
                // Signatários internos: rep. legal assina e testemunhas testemunham junto com o associado
                procurador: signatarios.procurador,
                testemunha1: signatarios.testemunha1,
                testemunha2: signatarios.testemunha2,
                ucs_antigas: antigasValidas.map(u => ({
                    numero: u.numero,
                    endereco: u.endereco,
                    data_desvinculacao: dataTransferencia
                })),
                ucs_novas: novasValidas.map(u => ({
                    numero: u.numero,
                    endereco: u.endereco,
                    data_vinculacao: dataTransferencia
                })),
                // Alteração de desconto (tipo_aditivo = 'desconto'); percentuais em escala 0–100
                ucs_desconto: ucsDesconto.map(u => ({ numero: u.numero, endereco: u.endereco })),
                desconto_atual_tarifa: descontoAtualTarifa,
                desconto_atual_bandeira: descontoAtualBandeira,
                desconto_atual_texto: ehDesconto ? textoDescontoAtual : '',
                desconto_novo_tarifa: ehDesconto ? novoTarifaNum : null,
                desconto_novo_bandeira: ehDesconto ? novoBandeiraNum : null,
                desconto_novo_texto: ehDesconto ? textoDescontoNovo : '',
                plano_novo: ehDesconto ? (planoNovo?.label || 'Fora da política') : '',
                data_vigencia_desconto: ehDesconto ? dataTransferencia : '',
                // Chaves legadas (compatibilidade com versão anterior do fluxo n8n)
                uc01: antigasValidas[0]?.numero || novasValidas[0]?.numero || ucsDesconto[0]?.numero || '',
                uc01_2: antigasValidas[1]?.numero || ucsDesconto[1]?.numero || '',
                endereco_uc01_2: antigasValidas[1]?.endereco || ucsDesconto[1]?.endereco || '',
                rua: novasValidas[0]?.endereco || ''
            };

            const response = await fetch(webhookUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const responseText = await response.text();
                let n8nResponse: any = null;
                if (responseText) {
                    try {
                        n8nResponse = JSON.parse(responseText);
                    } catch {
                        console.warn('Resposta do webhook não é JSON:', responseText);
                    }
                }

                if (n8nResponse && n8nResponse.link_assinatura_cliente) {
                    setSuccessData({
                        link: n8nResponse.link_assinatura_cliente,
                        numero: n8nResponse.numero_termo || ''
                    });
                    logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'gerar_aditivo_contratual', origem: 'acao' });

                    try {
                        await supabase.from('termo_aditivo_logs').insert({
                            uc_id: antigasValidas[0]?.ucId || ucsDesconto[0]?.ucId || ucsSelecionadas[0]?.id || null,
                            uc_numero: (ehDesconto ? ucsDesconto : (antigasValidas.length > 0 ? antigasValidas : novasValidas)).map(u => u.numero).join(', '),
                            titular_anterior: titularAtualNome,
                            novo_titular: novoTitularNome,
                            cpf_cnpj_novo_titular: novoTitularCpfCnpj,
                            email_novo_titular: email,
                            whatsapp_novo_titular: whatsapp,
                            // PDF público do Autentique — passa a existir quando todos assinam (o link de assinatura expira)
                            url_pdf: n8nResponse.id_autentique
                                ? `https://api.autentique.com.br/documentos/${n8nResponse.id_autentique}/assinado.pdf`
                                : (n8nResponse.url_pdf || n8nResponse.link_assinatura_cliente),
                            id_autentique: n8nResponse.id_autentique || null,
                            numero_termo: n8nResponse.numero_termo || null,
                            user_id: user?.id || null,
                            tipo_cliente: tipoCliente,
                            tipo_aditivo: tipoAditivo
                        });
                    } catch (logError) {
                        console.error('Error logging aditivo term:', logError);
                    }

                    if (ehDesconto) {
                        if (aplicarDescontoNoCadastro) {
                            await sincronizarDesconto(ucsDesconto, n8nResponse.numero_termo || '');
                        } else {
                            addToast('Aditivo gerado. O novo desconto NÃO foi aplicado no cadastro — altere em Unidades quando o cliente assinar.', 'info');
                        }
                    } else {
                        await sincronizarUnidades(antigasValidas, novasValidas, n8nResponse.numero_termo || '');
                    }

                    addToast('Aditivo Contratual gerado com sucesso!', 'success');
                } else {
                    throw new Error('Retorno inválido do webhook — Link não encontrado na resposta.');
                }
            } else {
                throw new Error(`Erro ${response.status}: ${response.statusText}`);
            }

        } catch (error: any) {
            console.error('Erro ao gerar aditivo contratual:', error);
            addToast('Erro ao gerar aditivo: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = () => {
        if (successData?.link) {
            navigator.clipboard.writeText(successData.link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const renderUcEntries = (
        entries: UCEntry[],
        setter: React.Dispatch<React.SetStateAction<UCEntry[]>>,
        addLabel: string
    ) => (
        <div className="space-y-3">
            {entries.map((entry, idx) => (
                <div key={idx} className="bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 rounded-xl p-3 space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">UC {idx + 1}</p>
                        {entries.length > 1 && (
                            <button
                                type="button"
                                onClick={() => setter(prev => prev.filter((_, i) => i !== idx))}
                                className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Número da UC *</label>
                            <input type="text" value={entry.numero} onChange={e => updateEntry(setter, idx, 'numero', e.target.value)} className={inputCls} />
                        </div>
                        <div className="sm:col-span-1">
                            <label className={labelCls}>Endereço Completo da UC</label>
                            <input type="text" value={entry.endereco} onChange={e => updateEntry(setter, idx, 'endereco', e.target.value)} placeholder="Rua, nº, bairro, cidade/UF" className={inputCls} />
                        </div>
                    </div>
                </div>
            ))}
            {entries.length < MAX_UCS && (
                <button
                    type="button"
                    onClick={() => setter(prev => [...prev, emptyEntry()])}
                    className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                >
                    <Plus size={15} /> {addLabel}
                </button>
            )}
        </div>
    );

    const renderTermoBanner = () => {
        if (!termoInfo) return null;
        if (termoInfo.status === 'assinado') {
            return (
                <div className="flex items-start gap-2.5 bg-green-50 dark:bg-green-900/15 border border-green-200 dark:border-green-800/50 rounded-xl p-3">
                    <CheckCircle2 size={16} className="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-green-800 dark:text-green-300">
                        Termo de adesão original assinado em <strong>{fmtBR(termoInfo.dataAssinatura || '')}</strong>. Data preenchida automaticamente.
                    </p>
                </div>
            );
        }
        if (termoInfo.status === 'nao_assinado') {
            return (
                <div className="flex items-start gap-2.5 bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800/50 rounded-xl p-3">
                    <AlertTriangle size={16} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-800 dark:text-red-300">
                        <strong>O termo de adesão original desta UC ainda não foi assinado.</strong> Um aditivo só faz sentido sobre um contrato vigente — verifique a situação da negociação antes de prosseguir.
                    </p>
                </div>
            );
        }
        return (
            <div className="flex items-start gap-2.5 bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 rounded-xl p-3">
                <Info size={16} className="text-gray-500 dark:text-slate-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-600 dark:text-slate-300">
                    Negociação/termo original não localizado no sistema para esta UC. Informe a data do termo manualmente e confirme se ele foi assinado.
                </p>
            </div>
        );
    };

    return createPortal(
        <>
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsDoFundo}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg border border-blue-100 dark:border-blue-800/50">
                            <FilePlus2 size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">Gerar Aditivo Contratual</h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400">Transferência, adição de UC ou alteração de desconto no termo de adesão.</p>
                        </div>
                    </div>
                    <button
                        onClick={pedirParaFechar}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {successData ? (
                        <div className="flex flex-col items-center justify-center text-center space-y-4 py-6">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mb-2">
                                <Check size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">Aditivo Contratual Gerado!</h3>
                            <p className="text-sm text-gray-500 dark:text-slate-400 max-w-sm">
                                {ehDesconto
                                    ? (aplicarDescontoNoCadastro
                                        ? 'O termo aditivo foi enviado para assinatura via Autentique e o novo desconto já foi aplicado nas UCs.'
                                        : 'O termo aditivo foi enviado para assinatura via Autentique. O novo desconto ainda NÃO foi aplicado no cadastro.')
                                    : 'O termo aditivo foi enviado para assinatura via Autentique e as UCs foram sincronizadas no sistema.'} O titular receberá o link por WhatsApp e e-mail.
                            </p>
                            {successData.numero && (
                                <p className="text-xs text-gray-400 dark:text-slate-500">
                                    N° do termo: <span className="font-mono font-semibold">{successData.numero}</span>
                                </p>
                            )}

                            <div className="w-full bg-gray-50 dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700 mt-4 flex items-center justify-between">
                                <span className="text-sm text-gray-600 dark:text-slate-300 truncate mr-4">{successData.link}</span>
                                <button
                                    onClick={handleCopyLink}
                                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm"
                                >
                                    {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                                    {copied ? 'Copiado!' : 'Copiar'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form id="aditivo-form" onSubmit={handleGenerateAditivo} onChange={() => setRascunho(true)} className="space-y-6">

                            {/* UCs pendentes de cadastro */}
                            {ucsFaltantes.length > 0 && (
                                <div className="bg-amber-50 dark:bg-amber-900/15 border border-amber-300 dark:border-amber-700/50 rounded-xl p-4 space-y-3">
                                    <div className="flex items-start gap-2.5">
                                        <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                                        <p className="text-xs text-amber-800 dark:text-amber-300">
                                            <strong>UC(s) não cadastradas no sistema.</strong> Para manter a base coerente, cadastre cada uma pelo formulário completo de Unidade Consumidora — os dados do cliente e do plano já vêm pré-preenchidos a partir da UC base.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        {ucsFaltantes.map(f => (
                                            <div key={`${f.tipo}-${f.numero}`} className="flex items-center justify-between gap-3 bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-800/40 rounded-lg px-3 py-2">
                                                <span className="text-sm text-gray-700 dark:text-slate-200">
                                                    UC <strong className="font-mono">{f.numero}</strong> — {f.tipo === 'nova' ? 'nova (a vincular)' : f.tipo === 'desconto' ? 'abrangida pelo aditivo' : 'antiga (a desvincular)'}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => abrirCadastroUC(f)}
                                                    className="flex-shrink-0 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-bold transition-colors"
                                                >
                                                    Cadastrar UC
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tipo de Aditivo */}
                            <div className="space-y-3">
                                <h3 className={sectionTitleCls}>
                                    <FileText size={16} className="text-blue-500" /> Tipo de Aditivo
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => alterarTipoAditivo('transferencia')}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                                            tipoAditivo === 'transferencia'
                                                ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <ArrowRightLeft size={16} className={`mt-0.5 flex-shrink-0 ${tipoAditivo === 'transferencia' ? 'text-blue-500' : 'text-gray-400'}`} />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Transferência de UC</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Desvincula UC(s) antiga(s) e vincula nova(s) no lugar.</p>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => alterarTipoAditivo('adicao')}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                                            tipoAditivo === 'adicao'
                                                ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <PlusCircle size={16} className={`mt-0.5 flex-shrink-0 ${tipoAditivo === 'adicao' ? 'text-blue-500' : 'text-gray-400'}`} />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Apenas Adição de UC</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Vincula 1 ou 2 UCs novas sem desvincular nenhuma.</p>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => alterarTipoAditivo('desconto')}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                                            ehDesconto
                                                ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <Percent size={16} className={`mt-0.5 flex-shrink-0 ${ehDesconto ? 'text-blue-500' : 'text-gray-400'}`} />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Alteração de Desconto</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Muda o desconto das UCs já vinculadas ao contrato.</p>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            {/* Modo de preenchimento */}
                            <div className="space-y-3">
                                <h3 className={sectionTitleCls}>
                                    <Search size={16} className="text-blue-500" /> Origem dos Dados
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setModo('buscar')}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                                            modo === 'buscar'
                                                ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <Search size={16} className={`mt-0.5 flex-shrink-0 ${modo === 'buscar' ? 'text-blue-500' : 'text-gray-400'}`} />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Buscar UC Existente</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Preencher a partir de UC(s) cadastrada(s) do cliente.</p>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setModo('manual')}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                                            modo === 'manual'
                                                ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <PencilLine size={16} className={`mt-0.5 flex-shrink-0 ${modo === 'manual' ? 'text-blue-500' : 'text-gray-400'}`} />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Preencher Manualmente</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Digitar todas as informações do termo.</p>
                                        </div>
                                    </button>
                                </div>
                                {modo === 'buscar' && (
                                    <UCSearchPicker
                                        selecionadas={ucsSelecionadas}
                                        onAdd={handleAddUcSelecionada}
                                        onRemove={handleRemoveUcSelecionada}
                                        max={tipoAditivo === 'adicao' ? 1 : MAX_UCS}
                                        placeholder={ehDesconto
                                            ? 'Buscar a UC que terá o desconto alterado...'
                                            : tipoAditivo === 'transferencia'
                                                ? 'Buscar UC antiga por cliente, nº da UC ou CPF/CNPJ...'
                                                : 'Buscar UC do cliente para puxar os dados do contrato...'}
                                    />
                                )}
                            </div>

                            {/* Titular Atual */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <User size={16} className="text-blue-500" /> Titular Atual
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelCls}>Nome / Razão Social</label>
                                        <input
                                            type="text"
                                            value={titularAtualNome}
                                            onChange={e => setTitularAtualNome(e.target.value)}
                                            readOnly={modo === 'buscar' && ucsSelecionadas.length > 0}
                                            className={`${inputCls} ${modo === 'buscar' && ucsSelecionadas.length > 0 ? 'opacity-70' : ''}`}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>CPF / CNPJ</label>
                                        <input
                                            type="text"
                                            value={titularAtualCpfCnpj}
                                            onChange={e => setTitularAtualCpfCnpj(e.target.value)}
                                            readOnly={modo === 'buscar' && ucsSelecionadas.length > 0}
                                            className={`${inputCls} ${modo === 'buscar' && ucsSelecionadas.length > 0 ? 'opacity-70' : ''}`}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Novo Titular */}
                            <div className="space-y-4">
                                <div className={sectionTitleCls + ' justify-between'}>
                                    <span className="flex items-center gap-2">
                                        <User size={16} className="text-blue-500" /> Titular do Aditivo (Assinará o Termo)
                                    </span>
                                    <button
                                        type="button"
                                        onClick={copiarTitularAtual}
                                        className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors normal-case"
                                    >
                                        Copiar do titular atual
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-slate-400 -mt-2">
                                    Pré-preenchido com o titular atual. Altere apenas se o aditivo envolver troca de titularidade.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className={labelCls}>Nome Completo / Razão Social *</label>
                                        <input type="text" required value={novoTitularNome} onChange={e => setNovoTitularNome(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Tipo *</label>
                                        <select
                                            value={tipoCliente}
                                            onChange={e => {
                                                setTipoCliente(e.target.value as 'PF' | 'PJ');
                                                setNovoTitularCpfCnpj('');
                                            }}
                                            className={inputCls}
                                        >
                                            <option value="PF">Pessoa Física</option>
                                            <option value="PJ">Pessoa Jurídica</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelCls}>CPF / CNPJ *</label>
                                        <input
                                            type="text"
                                            required
                                            value={novoTitularCpfCnpj}
                                            onChange={handleCpfCnpjChange}
                                            className={inputCls}
                                            placeholder={tipoCliente === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>E-mail *</label>
                                        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>WhatsApp *</label>
                                        <input type="text" required value={whatsapp} onChange={e => setWhatsapp(e.target.value)} className={inputCls} />
                                    </div>
                                </div>
                            </div>

                            {/* Dados do Termo */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <FileText size={16} className="text-blue-500" /> Dados do Termo
                                </h3>
                                {renderTermoBanner()}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="relative">
                                        <label className={labelCls}>
                                            Data do Termo Original *
                                            <button
                                                type="button"
                                                onClick={() => toggleTooltip('termoOriginal')}
                                                onMouseEnter={() => setActiveTooltip('termoOriginal')}
                                                onMouseLeave={() => setActiveTooltip(null)}
                                                className="inline-flex ml-1.5 text-gray-400 hover:text-blue-500 transition-colors align-middle"
                                                aria-label="O que é a Data do Termo Original?"
                                            >
                                                <Info size={13} />
                                            </button>
                                        </label>
                                        {activeTooltip === 'termoOriginal' && (
                                            <div className="absolute z-50 left-0 top-full mt-1 w-72 bg-gray-900 dark:bg-slate-700 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl border border-gray-700 dark:border-slate-600 leading-relaxed animate-fade-in">
                                                <p className="font-semibold mb-1">📄 Data do Termo de Adesão Original</p>
                                                <p>É a data em que o cliente assinou o Termo de Adesão principal junto à associação. O aditivo faz referência a este contrato original para aplicar a transferência.</p>
                                            </div>
                                        )}
                                        <input type="date" required value={dataTermoOriginal} onChange={e => setDataTermoOriginal(e.target.value)} className={inputCls} />
                                    </div>
                                    <div className="relative">
                                        <label className={labelCls}>
                                            {ehDesconto ? 'Data de Vigência *' : 'Data da Transferência *'}
                                            <button
                                                type="button"
                                                onClick={() => toggleTooltip('dataTransferencia')}
                                                onMouseEnter={() => setActiveTooltip('dataTransferencia')}
                                                onMouseLeave={() => setActiveTooltip(null)}
                                                className="inline-flex ml-1.5 text-gray-400 hover:text-blue-500 transition-colors align-middle"
                                                aria-label="O que é esta data?"
                                            >
                                                <Info size={13} />
                                            </button>
                                        </label>
                                        {activeTooltip === 'dataTransferencia' && (
                                            <div className="absolute z-50 left-0 top-full mt-1 w-72 bg-gray-900 dark:bg-slate-700 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl border border-gray-700 dark:border-slate-600 leading-relaxed animate-fade-in">
                                                {ehDesconto ? (
                                                    <>
                                                        <p className="font-semibold mb-1">📅 Data de Vigência</p>
                                                        <p>A partir de quando o novo desconto passa a valer. É a data que consta no aditivo e a referência para as faturas emitidas daí em diante.</p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="font-semibold mb-1">📅 Data da Transferência</p>
                                                        <p>Data em que a transferência das UCs será efetivada. Esta mesma data será usada como <strong>data de desvinculação</strong> das UCs antigas e <strong>data de vinculação</strong> das UCs novas no documento gerado.</p>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        <input type="date" required value={dataTransferencia} onChange={e => setDataTransferencia(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Concessionária</label>
                                        <input type="text" value={concessionaria} onChange={e => setConcessionaria(e.target.value)} className={inputCls} />
                                    </div>
                                </div>
                            </div>

                            {/* UCs Antigas — apenas em transferência */}
                            {tipoAditivo === 'transferencia' && (
                                <div className="space-y-4">
                                    <h3 className={sectionTitleCls}>
                                        <Zap size={16} className="text-orange-500" /> UCs Antigas (a Desvincular)
                                    </h3>
                                    {renderUcEntries(ucsAntigas, setUcsAntigas, 'Adicionar segunda UC antiga')}
                                </div>
                            )}

                            {/* UCs abrangidas — apenas em alteração de desconto */}
                            {ehDesconto && (
                                <div className="space-y-4">
                                    <h3 className={sectionTitleCls}>
                                        <Zap size={16} className="text-blue-500" /> UCs Abrangidas pelo Aditivo
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-slate-400 -mt-2">
                                        UCs do contrato que passam a ter o novo desconto. O desconto atual exibido abaixo é o da primeira UC selecionada.
                                    </p>
                                    {renderUcEntries(ucsAntigas, setUcsAntigas, 'Adicionar segunda UC')}
                                </div>
                            )}

                            {/* Alteração de Desconto */}
                            {ehDesconto && (
                                <div className="space-y-4">
                                    <h3 className={sectionTitleCls}>
                                        <Percent size={16} className="text-blue-500" /> Novo Desconto
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 rounded-xl p-3">
                                            <p className="text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1">Desconto atual</p>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">{textoDescontoAtual}</p>
                                        </div>
                                        <div className="bg-blue-50/50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/50 rounded-xl p-3">
                                            <p className="text-[11px] font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider mb-1">Novo desconto</p>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">{textoDescontoNovo || '—'}</p>
                                            <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-0.5">
                                                {planoNovo ? planoNovo.label : 'Selecione o plano ou informe os percentuais.'}
                                            </p>
                                        </div>
                                    </div>

                                    <div>
                                        <label className={labelCls}>Plano da Política Comercial</label>
                                        <select
                                            value={planoNovo?.codigo || ''}
                                            onChange={e => {
                                                const p = planosSelecionaveis().find(x => x.codigo === e.target.value);
                                                if (!p || p.foraDaPolitica) {
                                                    setDescontoNovoTarifa('');
                                                    setDescontoNovoBandeira('');
                                                    return;
                                                }
                                                setDescontoNovoTarifa(String(p.descTarifa));
                                                setDescontoNovoBandeira(String(p.descBandeira));
                                            }}
                                            className={inputCls}
                                        >
                                            <option value="">Selecione o plano (ou preencha os percentuais abaixo)</option>
                                            {planosSelecionaveis().map(p => (
                                                <option key={p.codigo} value={p.codigo}>{p.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelCls}>Novo Desc. Tarifa (%) *</label>
                                            <input type="number" min="0" max="100" value={descontoNovoTarifa} onChange={e => setDescontoNovoTarifa(e.target.value)} placeholder="22" className={inputCls} />
                                        </div>
                                        <div>
                                            <label className={labelCls}>Novo Desc. Bandeira (%) *</label>
                                            <input type="number" min="0" max="100" value={descontoNovoBandeira} onChange={e => setDescontoNovoBandeira(e.target.value)} placeholder="50" className={inputCls} />
                                        </div>
                                    </div>

                                    {descontoNovoPreenchido && !planoNovo && (
                                        <div className="flex items-start gap-2.5 bg-orange-50 dark:bg-orange-900/15 border border-orange-200 dark:border-orange-800/50 rounded-xl p-3">
                                            <AlertTriangle size={16} className="text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                                            <p className="text-xs text-orange-800 dark:text-orange-300">
                                                Estes percentuais não correspondem a nenhum plano oficial — condição <strong>fora da política comercial</strong>. Confirme a aprovação antes de gerar o aditivo.
                                            </p>
                                        </div>
                                    )}

                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={aplicarDescontoNoCadastro}
                                            onChange={e => setAplicarDescontoNoCadastro(e.target.checked)}
                                            className="mt-0.5 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500/40"
                                        />
                                        <span className="text-xs text-gray-600 dark:text-slate-300">
                                            Aplicar o novo desconto no cadastro das UCs assim que o aditivo for gerado. Desmarque se o desconto só deve valer depois da assinatura — nesse caso, altere manualmente em Unidades.
                                        </span>
                                    </label>
                                </div>
                            )}

                            {/* UCs Novas */}
                            {!ehDesconto && (
                                <div className="space-y-4">
                                    <h3 className={sectionTitleCls}>
                                        <Zap size={16} className="text-green-500" /> UCs Novas (a Vincular)
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-slate-400 -mt-2">
                                        Toda UC do termo precisa existir em Unidades Consumidoras — se não existir, você será orientado a cadastrá-la pelo formulário completo. Após gerar, as novas ficam Ativas no nome do titular{tipoAditivo === 'transferencia' ? ' e as antigas são marcadas como Canceladas' : ''}.
                                    </p>
                                    {renderUcEntries(ucsNovas, setUcsNovas, 'Adicionar segunda UC nova')}
                                </div>
                            )}

                            {/* Saldo de Créditos */}
                            {!ehDesconto && (
                                <div className="space-y-4">
                                    <h3 className={sectionTitleCls}>
                                        <Coins size={16} className="text-blue-500" /> Saldo de Créditos da(s) UC(s) Antiga(s)
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelCls}>Saldo Acumulado (kWh)</label>
                                            <input type="text" value={saldoKwh} onChange={e => setSaldoKwh(e.target.value)} placeholder="Ex: 1.250" className={inputCls} />
                                        </div>
                                        <div>
                                            <label className={labelCls}>Valor Estimado (R$)</label>
                                            <input type="text" value={valorSaldo} onChange={e => setValorSaldo(e.target.value)} placeholder="Ex: 1.180,50" className={inputCls} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Signatários internos */}
                            <SignatariosSelector
                                comProcurador
                                comTestemunhas
                                value={signatarios}
                                onChange={setSignatarios}
                            />

                            {/* Observações */}
                            <div>
                                <label className={labelCls}>Observações (Opcional)</label>
                                <textarea
                                    value={obs}
                                    onChange={e => setObs(e.target.value)}
                                    rows={3}
                                    placeholder="Informações adicionais para constar no termo aditivo..."
                                    className={`${inputCls} resize-y`}
                                />
                            </div>

                        </form>
                    )}
                </div>

                {/* Footer */}
                {!successData && (
                    <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={pedirParaFechar}
                            disabled={loading}
                            className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            form="aditivo-form"
                            disabled={loading}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-colors shadow-sm disabled:opacity-70 flex items-center justify-center min-w-[160px]"
                        >
                            {loading ? <Loader2 size={16} className="animate-spin" /> : 'Gerar Aditivo'}
                        </button>
                    </div>
                )}
            </div>

            {/* Cadastro completo de UC inexistente (formulário do CRM, pré-preenchido) */}
            <UnidadeModal
                isOpen={showCadastroUC}
                onClose={() => {
                    setShowCadastroUC(false);
                    setCadastroPrefill(null);
                }}
                prefill={cadastroPrefill}
                onSave={handleCadastroSalvo}
            />
        </div>

        <ConfirmModal
            isOpen={confirmandoDescarte}
            onClose={continuarPreenchendo}
            onConfirm={descartarEFechar}
            title="Descartar o aditivo?"
            message="Você já preencheu dados deste aditivo contratual. Se fechar agora, tudo o que foi digitado se perde."
            confirmLabel="Descartar"
            cancelLabel="Continuar preenchendo"
            variant="warning"
        />
        </>,
        document.body
    );
};

export default AditivoContratualModal;
